import { useEffect, useState } from "react";
import { products } from "@/data/products";
import { Mail, Phone, MapPin, Send, Copy, Check, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import officeMap from "@/assets/office-map.svg";

const CopyButton = ({ value, label }: { value: string; label: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`Copied ${label}`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(`Could not copy ${label}`);
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={`Copy ${label}`}
      title={`Copy ${label}`}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
};


const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Lightweight validation (replaces zod to keep it out of the page bundle).
export const validateContact = (d: { name: string; email: string; company: string; product: string; message: string }): string | null => {
  const name = d.name.trim(), email = d.email.trim(), message = d.message.trim();
  if (!name) return "Name is required";
  if (name.length > 100) return "Name is too long";
  if (!EMAIL_RE.test(email) || email.length > 255) return "Invalid email";
  if (d.company.trim().length > 100) return "Company is too long";
  if (d.product.length > 50) return "Invalid product";
  if (!message) return "Message is required";
  if (message.length > 1000) return "Message is too long";
  return null;
};

export const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState("");

  useEffect(() => {
    const slug = new URLSearchParams(window.location.search).get("product");
    if (slug && products.some((p) => p.slug === slug)) setProduct(slug);
  }, []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const data = {
      name: String(fd.get("name") || ""),
      email: String(fd.get("email") || ""),
      company: String(fd.get("company") || ""),
      product: products.find((p) => p.slug === product)?.name ?? "",
      message: String(fd.get("message") || ""),
    };
    const err = validateContact(data);
    if (err) {
      toast.error(err);
      return;
    }

    const params = new URLSearchParams();
    Object.entries(data).forEach(([k, v]) => params.append(k, v));
    params.append("url", window.location.href);
    params.append("subject", "Info request from Bane Tech website");

    setLoading(true);
    window.umami?.track("contact_form_submit", { product: product || "none" });
    try {
      const res = await fetch("mail.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const text = (await res.text()).trim();
      const num = Number(text);
      if (!Number.isNaN(num) && num > 0) {
        toast.success("Message sent. We'll get back to you within 1 business day.");
        window.umami?.track("contact_form_success", { product: product || "none" });
        form.reset();
        setProduct("");
      } else {
        toast.error(text || "Failed to send message. Please try again.");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-surface/40 border-t border-border">
      <div className="container">
        <div className="max-w-2xl mb-16">
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Contact</p>
          <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight">
            Talk to our engineering team.
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">
            Tell us about your systems and what you're trying to achieve. We typically reply within one business day.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Info + map */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl border border-border bg-card-gradient p-6 space-y-5">
              <div className="flex items-start gap-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/30">
                  <MapPin className="h-4 w-4 text-primary" />
                </span>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Address</p>
                  <p className="text-sm mt-1">Alameda dos Oceanos, 25 - Escritório 2, Parque das Nações<br/>1990-196 Lisboa, Portugal</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/30">
                  <Mail className="h-4 w-4 text-primary" />
                </span>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Email</p>
                  <div className="mt-1 flex items-center gap-2">
                    <a href="mailto:info@banetech.net" className="text-sm hover:text-primary transition-colors">
                      info@banetech.net
                    </a>
                    <CopyButton value="info@banetech.net" label="email" />
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/30">
                  <Phone className="h-4 w-4 text-primary" />
                </span>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Phone</p>
                  <div className="mt-1 flex items-center gap-2">
                    <a href="tel:+351210123010" className="text-sm hover:text-primary transition-colors">
                      +351 210 123 010
                    </a>
                    <CopyButton value="+351 210 123 010" label="phone number" />
                  </div>
                </div>
              </div>
            </div>

            <a
              href="https://www.google.com/maps/search/?api=1&query=Bane+Tech+Alameda+dos+Oceanos+25+Lisboa"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open Bane Tech location in Google Maps"
              onClick={() => window.umami?.track("map_open")}
              className="group relative block rounded-2xl border border-border overflow-hidden h-64 bg-surface"
            >
              <img
                src={officeMap}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                width={1000}
                height={600}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full flex flex-col items-center">
                <span className="mb-1 rounded-md border border-border bg-background/90 px-2 py-0.5 font-display text-xs font-semibold shadow-elevated backdrop-blur">
                  Bane Tech
                </span>
                <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary shadow-glow">
                  <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping motion-reduce:hidden" />
                  <MapPin className="relative h-4 w-4 text-primary-foreground" />
                </span>
              </div>
              <div className="absolute inset-x-3 bottom-3 flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-background/85 px-4 py-2.5 shadow-elevated backdrop-blur-md">
                <div className="min-w-0">
                  <p className="font-display text-sm font-semibold">Bane Tech Headquarters</p>
                  <p className="text-xs text-muted-foreground truncate">Alameda dos Oceanos 25, Lisboa</p>
                </div>
                <span className="inline-flex shrink-0 items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-opacity group-hover:opacity-90">
                  Open in Google Maps <ExternalLink className="h-4 w-4" />
                </span>
              </div>
              <span className="absolute right-2 top-2 rounded bg-background/80 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                © OpenStreetMap contributors
              </span>
            </a>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="lg:col-span-3 rounded-2xl border-gradient bg-card-gradient p-1">
            <div className="rounded-[14px] bg-surface p-6 md:p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" placeholder="Jane Doe" required maxLength={100} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Work email</Label>
                  <Input id="email" name="email" type="email" placeholder="jane@company.com" required maxLength={255} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input id="company" name="company" placeholder="Acme Corp" maxLength={100} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="product">Product of interest <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <select
                  id="product"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Not sure yet / General enquiry</option>
                  {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">How can we help?</Label>
                <Textarea id="message" name="message" rows={6} placeholder="Tell us about your project, scale and timeline." required maxLength={1000} />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <Button type="submit" variant="hero" size="lg" disabled={loading} className="w-full sm:w-auto">
                  {loading ? "Sending…" : <>Send message <Send className="ml-2 h-4 w-4" /></>}
                </Button>
                <p className="text-sm text-muted-foreground">We reply within 1 business day.</p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
